// Ringing coordination for the renderer.
// - Desktop app (bridge present): the Electron background process rings; we only mirror its state.
// - Web: while this tab is open we heartbeat every 30s; the server grants one device the ringing lease
//   and tells it when to ring. Sound needs a user gesture first ("Enable sound in this tab").
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AttentionItem, DesktopRingState, RingDirective, RingtoneId } from '@mailpilot/shared';
import { api } from './api';
import { bridge, deviceName } from './desktop';
import { audioUnlocked, playRingtone, unlockAudio } from './sound';
import { useMe } from '../hooks/useMe';
import { useAttentionItems } from '../hooks/useAttention';

export type NotificationPerm = NotificationPermission | 'unsupported';

interface RingContext {
  mode: 'desktop' | 'web';
  soundEnabled: boolean;
  enableSound: () => Promise<boolean>;
  notificationPermission: NotificationPerm;
  requestNotificationPermission: () => Promise<void>;
  desktopState: DesktopRingState | null;
  directive: RingDirective | null;
  /** Items in the current visible alert session (empty = no alert). */
  alertItems: AttentionItem[];
  ringingNow: boolean;
  dismissAlert: () => void;
  stopSound: () => void;
  testSound: (ringtone: RingtoneId, volume: number, durationSec: number) => Promise<void>;
  unavailableReason: string | null;
  pendingCount: number;
}

const Ctx = createContext<RingContext | null>(null);

const HEARTBEAT_MS = 30_000;

export function RingProvider({ children }: { children: ReactNode }) {
  const b = bridge();
  const me = useMe().data;
  const settings = me?.settings;
  const attention = useAttentionItems('open');
  const pendingItems = useMemo(() => (attention.data?.items ?? []).filter((i) => i.status === 'pending'), [attention.data]);

  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => audioUnlocked());
  const [perm, setPerm] = useState<NotificationPerm>(() => (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission));
  const [desktopState, setDesktopState] = useState<DesktopRingState | null>(null);
  const [directive, setDirective] = useState<RingDirective | null>(null);
  const [alertItems, setAlertItems] = useState<AttentionItem[]>([]);
  const [ringingNow, setRingingNow] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // ----- desktop mirror -----
  useEffect(() => {
    if (!b) return;
    let off: (() => void) | undefined;
    b.getRingState().then(setDesktopState).catch(() => undefined);
    try {
      off = b.onRingState(setDesktopState);
    } catch {
      /* ignore */
    }
    return () => off?.();
  }, [b]);

  // ----- web heartbeat -----
  const stopSound = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
    setRingingNow(false);
    if (b) void b.stopRinging().catch(() => undefined);
  }, [b]);

  const ringNow = useCallback(async (d: RingDirective) => {
    const s = settingsRef.current;
    if (!s) return;
    setAlertItems(d.items);
    stopRef.current?.();
    stopRef.current = playRingtone(s.ring.ringtone, s.ring.volume, s.ring.ringDurationSeconds);
    setRingingNow(true);
    setTimeout(() => setRingingNow(false), s.ring.ringDurationSeconds * 1000);
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const n = new Notification(d.items.length > 1 ? `${d.items.length} items need your attention` : d.items[0]?.title ?? 'MailPilot', {
          body: d.items.length > 1 ? 'Open MailPilot to acknowledge.' : d.items[0]?.body,
          tag: 'mailpilot-ring',
          requireInteraction: true,
        });
        // closing the notification does NOT acknowledge anything
        n.onclick = () => window.focus();
      } catch {
        /* ignore */
      }
    }
    try {
      await api('POST /api/ring/rang', { body: { itemIds: d.items.map((i) => i.id) } });
    } catch {
      /* best effort */
    }
  }, []);

  const tick = useCallback(async () => {
    if (b || !me) return;
    const s = settingsRef.current;
    if (!s) return;
    const name = await deviceName();
    let otherHolder: string | null = null;
    try {
      const st = await api('GET /api/ring/status');
      const other = st.devices.find((d) => d.holdsLease && d.sessionId !== me.sessionId && Date.now() - new Date(d.lastHeartbeatAt).getTime() < 120_000);
      otherHolder = other?.deviceName ?? null;
    } catch {
      /* status optional */
    }
    const enabled = audioUnlocked();
    const reason = !s.ring.enabled
      ? 'Ringing is turned off in Notification settings.'
      : !enabled
        ? 'Sound is not enabled in this tab yet (browsers block audio until you click "Enable sound").'
        : otherHolder
          ? `${otherHolder} is currently responsible for ringing.`
          : null;
    const canRing = s.ring.enabled && enabled && !otherHolder;
    setUnavailableReason(reason);
    try {
      const d = await api('POST /api/ring/heartbeat', { body: { deviceName: name, canRing, unavailableReason: reason } });
      setDirective(d);
      if (d.shouldRing && d.items.length && canRing && !stopRef.current) void ringNow(d);
    } catch {
      /* offline: try again next tick */
    }
  }, [b, me, ringNow]);

  useEffect(() => {
    if (b || !me) return;
    void tick();
    const t = setInterval(() => void tick(), HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [b, me, tick]);

  // re-evaluate quickly when attention changes (e.g. new auto-send)
  const dataStamp = attention.dataUpdatedAt;
  useEffect(() => {
    if (!b && me && dataStamp) void tick();
  }, [dataStamp]); // eslint-disable-line react-hooks/exhaustive-deps

  // drop acknowledged items from a visible alert
  useEffect(() => {
    if (!alertItems.length) return;
    const ids = new Set(pendingItems.map((i) => i.id));
    const left = alertItems.filter((i) => ids.has(i.id));
    if (left.length !== alertItems.length) {
      setAlertItems(left);
      if (!left.length) stopSound();
    }
  }, [pendingItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // desktop: show the alert while the background process rings
  useEffect(() => {
    if (b && desktopState?.ringing && pendingItems.length) setAlertItems(pendingItems);
  }, [b, desktopState?.ringing, pendingItems]);

  const enableSound = useCallback(async () => {
    const ok = await unlockAudio();
    setSoundEnabled(ok);
    setTimeout(() => void tick(), 50);
    return ok;
  }, [tick]);

  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
    } catch {
      /* ignore */
    }
  }, []);

  const testSound = useCallback(
    async (ringtone: RingtoneId, volume: number, durationSec: number) => {
      if (b) {
        await b.testRing(ringtone, volume, durationSec);
        return;
      }
      const ok = await unlockAudio();
      setSoundEnabled(ok);
      stopRef.current?.();
      const stop = playRingtone(ringtone, volume, durationSec);
      stopRef.current = stop;
      setTimeout(() => {
        if (stopRef.current === stop) stopRef.current = null;
      }, durationSec * 1000 + 300);
    },
    [b],
  );

  const value: RingContext = {
    mode: b ? 'desktop' : 'web',
    soundEnabled,
    enableSound,
    notificationPermission: perm,
    requestNotificationPermission,
    desktopState,
    directive,
    alertItems,
    ringingNow: b ? !!desktopState?.ringing : ringingNow,
    dismissAlert: () => {
      // closing the alert does NOT acknowledge — items keep ringing on schedule
      stopSound();
      setAlertItems([]);
    },
    stopSound,
    testSound,
    unavailableReason: b ? desktopState?.unavailableReason ?? null : unavailableReason,
    pendingCount: pendingItems.length,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRing(): RingContext {
  const c = useContext(Ctx);
  if (!c) throw new Error('useRing outside RingProvider');
  return c;
}
